import type { Rectangle } from 'pixi.js';
import type {
    CanvasTextMetrics
} from '../../canvasText/metrics/CanvasTextMetrics';
import {
    tallestStrings
} from '../../canvasText/metrics/CanvasTextMetrics';
import { DynamicLevel, TextAlignment, SeekMode } from '../../../enums';
import type { CanvasTextStyleRendererSettings } from '../../canvasText/renderer/CanvasTextStyle';
import { CanvasTextStyleRenderer } from '../../canvasText/renderer/CanvasTextStyle';
import type { Transcript } from '../../text/Transcript';
import { getTotalBounds } from '../../canvasText/textMetricsHelpers';
import type { CanvasRendererSettings } from '../../canvasRenderer/renderer/CanvasRenderer';
import { CanvasRenderer } from '../../canvasRenderer/renderer/CanvasRenderer';
import type { CanvasTextDynamicGraphicRenderer } from '../../canvasText/renderer/CanvasTextDynamicGraphic';
import { buildMetricsForFancyCaptions } from '../metrics/CanvasFancyCaptionMetrics';

export interface CanvasFancyCaptionRendererSettings extends CanvasRendererSettings {
    transcript: Transcript;
    alignment: TextAlignment;
    wordWrap: boolean;
    style: CanvasTextStyleRendererSettings;
}

/**
 * Fancy captions render class. This class is responsible for animating and rendering the text and graphics from a transcript.
 */
export class CanvasFancyCaptionRenderer extends CanvasRenderer<CanvasFancyCaptionRendererSettings> {
    transcript: Transcript = null;

    #activeChunkIndex: number = 0;
    /** The build text metrics for each chunk of text. */
    #textMetrics: CanvasTextMetrics[] = [];
    /** The build style metrics for each chunk of text. */
    #styleMetrics: CanvasTextStyleRenderer[] = [];
    /** The bounds of the text block including the graphics for each chunk. */
    #textWithGraphicsBounds: Rectangle[] = [];
    #totalBounds: Rectangle = null;
    #animationDuration: number = null;
    #maxFirstLineAscent: number = null;

    wordWrap = false;
    alignment = TextAlignment.LEFT;
    style: CanvasTextStyleRenderer = new CanvasTextStyleRenderer();

    /** The total duration of all animation configurations */
    get animationDuration() {
        if (!this.#animationDuration) {
            this.#animationDuration = this.getAnimationDuration();
        }
        return this.#animationDuration;
    }

    getAnimationDuration() {
        let maxDuration = 0;
        const callback = (config) => {
            if (maxDuration < config.duration) {
                maxDuration = config.duration;
            }
        };
        this.style.animationConfigurations.forEach(callback);
        this.style.graphics.forEach(callback);
        return maxDuration;
    }

    get activeStyle(): CanvasTextStyleRenderer {
        if (this.#styleMetrics.length === 0) {
            throw new Error('styles is empty');
        }
        return this.#styleMetrics[this.activeIndex];
    }

    get activeIndex(): number {
        return this.#activeChunkIndex;
    }

    get activeText(): CanvasTextMetrics {
        if (this.#textMetrics.length === 0) {
            throw new Error('textMetrices is empty');
        }
        return this.#textMetrics[this.activeIndex];
    }

    /** Get the tallest text from all the chunks of text. */
    getTallestText() {
        if (!this.transcript) {
            throw new Error('no transcript');
        }
        return tallestStrings(this.#textMetrics
            .map(m => m.getTallestWordElements()))
            .join('');
    }

    /** Get the transcript word start and end time of the chunk at the specified index. */
    getTranscriptChunkTimeRange(chunkIndex: number) {
        if (this.#textMetrics.length === 0) {
            return { startTime: 0, endTime: 0 };
        }
        const [wordStartIndex, wordEndIndex] = this.#textMetrics[chunkIndex].sourceWordReferenceIndexRange;
        const startTime = this.transcript.words[wordStartIndex].startTime;
        const endTime = this.transcript.words[wordEndIndex].endTime;
        return { startTime, endTime };
    }

    /** Sets the active or visible text metrics based on the current time. **/
    override _onSetCurrentTime() {
        if (!this.transcript) {
            this.#activeChunkIndex = -1;
            return;
        }
        // keep the last chunk visible once we played back the entire transcript. This happens when the scene duration
        // is longer than the transcript
        if (this.currentTime >= this.transcript.endTime) {
            this.#activeChunkIndex = this.#textMetrics.length - 1;
            return;
        }

        const futureTime = (this.currentTime + this.chunkTimeOffset);
        let activeWordIndex = this.transcript.getActiveWordIndexAtTime(futureTime);

        // we don't have any words that are spoken at our future time
        if (activeWordIndex === -1) {
            // check if we have any at our current time. This ensures our last chunk lingers.
            activeWordIndex = this.transcript.getActiveWordIndexAtTime(this.currentTime);
            if (activeWordIndex === -1) {
                this.#activeChunkIndex = -1;
                return;
            }
        }
        // find the active word index in the text metrics
        this.#activeChunkIndex = this.#textMetrics
            .findIndex(m => {
                const [startIndex, endIndex] = m.sourceWordReferenceIndexRange;
                return startIndex <= activeWordIndex && endIndex >= activeWordIndex;
            });
    }

    /**
     * Depending on the seek mode, we need to resolve the time offset differently. If we're in single frame mode, then
     * we want to show the end frame of the chunk without any animation. If it's in can play mode, then we want to add
     * the animation duration to the end of the chunk so we can gradually animate to the next chunk.
     */
    get chunkTimeOffset(): number {
        return this.seekMode === SeekMode.ResolveWhenSingleFrame
            ? 0
            : this.animationDuration;
    }

    /** The bounds of the text block including the graphics. */
    get textWithGraphicsBounds(): Rectangle {
        return this.#textWithGraphicsBounds[this.activeIndex];
    }

    // @inheritdoc
    override get hasText(): boolean {
        return this.transcript.words.length > 0;
    }

    // @inheritdoc
    override get canRender(): boolean {
        // we can only render when we've built the text metrics and we seeked to an active index
        return this.#textMetrics.length > 0 && this.activeIndex !== -1;
    }

    get chunkCount(): number {
        return this.#textMetrics.length;
    }

    get hasChunks(): boolean {
        return this.#textMetrics.length > 0;
    }

    get chunks(): CanvasTextMetrics[] {
        return this.#textMetrics;
    }

    get totalBounds(): Rectangle {
        return this.#totalBounds;
    }

    get activeTextBounds(): Rectangle {
        return this.#textMetrics[this.activeIndex].bounds;
    }

    /**
     * The value which stabilizes the ascent of the first line of text. This ensures the baseline doesn't jump between
     * different chunks. We do this by removing the ascent of the first line of text from the max ascent of the first
     * line from all chunks.
     **/
    override get activeAscentStabilizer(): number {
        if (this.activeText.lines.length === 0) {
            return 0;
        }
        return this.maxFirstLineAscent - this.activeText.lines[0].ascent;
    }

    override apply(settings: CanvasFancyCaptionRendererSettings) {
        super.apply(settings);
        this.transcript = settings.transcript;
        this.alignment = settings.alignment;
        this.wordWrap = settings.wordWrap;
        this.style.apply(settings.style);
    }

    /** Build text, style and graphics metrics for each chunk of the transcript text */
    build() {
        if (!this.transcript) {
            return;
        }
        this.resetBuild();

        this.#textMetrics = buildMetricsForFancyCaptions({
            transcript: this.transcript,
            alignment: this.alignment,
            style: this.style,
            maxWidth: this.maxTextWidth,
            maxHeight: this.maxTextHeight,
        });

        if (this.#textMetrics.length === 0) {
            return;
        }

        this.#textMetrics.map((textMetrics) => {
            const bounds: Rectangle[] = [textMetrics.bounds];

            // create a clone of the style and build the graphics for the current text metrics to retrieve during draw.
            // remember that for our fancy captions we have a list of text metrics and thus a list of style metrics.
            // each one has unique metrics.
            const clone = this.style.clone();
            textMetrics.updateStyles(clone, clone);
            clone.graphics.forEach(graphic => {
                graphic.build(textMetrics);
                if (!graphic.asMask) bounds.push(graphic.bounds);
            });
            this.#styleMetrics.push(clone);
            this.#textWithGraphicsBounds.push(getTotalBounds(bounds));
        });

        this.#totalBounds = getTotalBounds(this.#textWithGraphicsBounds);
    }

    /**
     * Compute the animation values for each graphic and text element. This method requires currentTime, transcript and
     * activeText to be set to calculate the correct animation values.
     * */
    _computeAnimationValues() {
        // compute the animation values for each graphic
        this.activeStyle.graphics.forEach(graphic => {
            graphic.animationConfigurations.forEach(animation => {
                animation.computeTranscriptAnimationValues(this.currentTime, this.transcript, this.activeText);
            });
        });
        // compute the animation values our text
        this.activeStyle.animationConfigurations.forEach(animation => {
            animation.computeTranscriptAnimationValues(this.currentTime, this.transcript, this.activeText);
        });
    }

    get maxFirstLineAscent() {
        if (this.#maxFirstLineAscent === null) {
            this.#maxFirstLineAscent = this._maxFirstLineAscent();
        }
        return this.#maxFirstLineAscent;
    }

    _maxFirstLineAscent() {
        let maxBaseline = 0;
        this.#textMetrics.forEach(metrics => {
            if (maxBaseline < metrics.lines[0].ascent) {
                maxBaseline = metrics.lines[0].ascent;
            }
        });
        return maxBaseline;
    }

    /** Draw the text and style of the active chunk */
    override draw() {
        if (!this.canRender || this.transcript.empty) {
            this._resetCanvas();
            return;
        }
        this._computeAnimationValues();

        super.draw();

        this.context.save();
        this.#applyObjectScaleAnimation();
        this.#clipObject();
        this.#drawGraphics();
        this.#maskAndDrawText();
        this.context.restore();
    };

    /**
     * Apply any object scale animation to the canvas context. We do this here once globally instead of for each word
     * element for simplicity and performance.
     */
    #applyObjectScaleAnimation() {
        let scaleX = 1;
        let scaleY = 1;
        // scale from the center of the total bounds
        const scaleAnchorX = 0.5 * this.totalBounds.width;
        const scaleAnchorY = 0.5 * this.totalBounds.height;
        this.activeStyle.animationConfigurations
            .filter(config => config.level === DynamicLevel.object && (config.property === 'scaleX' || config.property === 'scaleY'))
            .forEach(config => {
                switch (config.property) {
                    case 'scaleX': scaleX = config.computedValues[0]; break;
                    case 'scaleY': scaleY = config.computedValues[0]; break;
                }
            });
        if (scaleX === 1 && scaleY === 1) {
            return;
        }
        this.context.translate(scaleAnchorX, scaleAnchorY);
        this.context.scale(scaleX, scaleY);
        this.context.translate(-scaleAnchorX, -scaleAnchorY);
    }

    /**
     * Transform the canvas to the active bounds. For FancyCaptions we need to use our total bounds transformation so
     * that we can align the active bounds within the total bounds thereby keeping their position in Y consistent
     * between chunks.
     */
    override _applyActiveBoundsTransformation() {
        // apply total bounds transformation
        const canvasAnchorX = -(this.anchorX * this.totalBounds.width);
        const canvasAnchorY = -(this.anchorY * this.totalBounds.height);
        this.context.translate(canvasAnchorX, canvasAnchorY);
        this.context.translate(this.translateX, this.translateY);
        this.context.translate(-this.totalBounds.x, -this.totalBounds.y);

        // align the active bounds within the total bounds
        const activeBoundsXOffset = (this.totalBounds.width - this.activeTextBounds.width) * this.anchorX;
        this.context.translate(activeBoundsXOffset, 0);

        // move the active bounds to the top border of the total bounds
        this.context.translate(0, this.totalBounds.y - this.activeTextBounds.y);

        // stabilize the baseline between the different chunks
        this.context.translate(0, this.activeAscentStabilizer);
    }

    /**
     * Apply the object masks, since they mask everything, including object level graphics.
     */
    #clipObject() {
        const objectMasks = this.#filterAndOrderGraphics({ level: DynamicLevel.object, mask: true });
        objectMasks.forEach(mask => mask.clipElement(this.context, 0));
    }

    /**
     * Draw all the graphics in level order of object, line, word.
     */
    #drawGraphics() {
        // Draw the graphics in level order: in the editor (and therefore scene design) the graphics can be created in
        // any level order. We need to extract them and draw them in the correct order.
        const objectGraphics = this.#filterAndOrderGraphics({ level: DynamicLevel.object, mask: false });
        const lineGraphics = this.#filterAndOrderGraphics({ level: DynamicLevel.line, mask: false });
        const wordGraphics = this.#filterAndOrderGraphics({ level: DynamicLevel.word, mask: false });

        objectGraphics.forEach(graphic => graphic.draw(this.context));
        lineGraphics.forEach(graphic => graphic.draw(this.context));
        wordGraphics.forEach(graphic => graphic.draw(this.context));
    };

    /**
     * Draw text, masking it appropriately at each of line and word levels.
     */
    #maskAndDrawText() {
        const lineMasks = this.#filterAndOrderGraphics({ level: DynamicLevel.line, mask: true });
        const wordMasks = this.#filterAndOrderGraphics({ level: DynamicLevel.word, mask: true });
        // Track the current word index, there is no 1:1 mapping from word masks to word metrics due to breaking spaces.
        let ixWord = 0;
        this.activeText.lines.forEach((line, ixLine) => {
            this.context.save();
            lineMasks.forEach(mask => mask.clipElement(this.context, ixLine));
            line.visibleWords.forEach((word) => {
                this.context.save();
                wordMasks.forEach(mask => mask.clipElement(this.context, ixWord));
                ixWord += 1;
                word.elements.forEach(element => {
                    element.draw(this.context);
                });
                this.context.restore();
            });
            this.context.restore();
        });
    };

    /**
     * Filter the graphics by level and mask status. Finally, will reverse the list so that we draw the graphics
     * from top to bottom. So the first graphic in the list will be the top-most graphic. This is how we present
     * the order in the editor.
     */
    #filterAndOrderGraphics({ level, mask }: { level: DynamicLevel; mask: boolean; }): CanvasTextDynamicGraphicRenderer[] {
        return this.activeStyle.graphics
            .filter(graphic => graphic.level === level && graphic.asMask === mask)
            .reverse();
    }

    override resetBuild() {
        this.#textMetrics = [];
        this.#totalBounds = null;
        this.#textWithGraphicsBounds = [];
        this.#activeChunkIndex = 0;
        this.#styleMetrics = [];
        this.#animationDuration = null;
        this.#maxFirstLineAscent = null;
    }
}
